// FiltersSheet — single dropdown that consolidates ALL non-member calendar
// filters into one button-triggered sheet.
//
// Why: per user feedback after Batch 44, the calendar header was crowded
// with persistent chips (member row + primitive-type row + sub-calendar
// row = up to 3 scrollable strips). User explicitly asked: ONLY member
// buttons stay permanent. Everything else goes behind ONE filter button
// that drops down with checkbox blocks.
//
// Renders two sections inside the sheet:
//   1. By type   — Events / Activities / Study / Chores / Todos / Polls
//   2. By calendar — Family + each sub-calendar (with "Manage…" link)
//
// State is owned by the parent (CalendarView) so the same filters survive
// view switches. Each section persists to localStorage independently.
import React, { useEffect, useState } from "react";
import { SlidersHorizontal, Plus, Settings2, X, Check } from "lucide-react";
import { BottomSheet } from "@/components/BottomSheet";
import { useAuth } from "@/context/AuthContext";
import { usePermission } from "@/hooks/usePermission";
import {
  listCalendars,
  getHiddenCalendarIds,
  setHiddenCalendarIds,
} from "@/lib/calendarsApi";
import { SubCalendarManageSheet } from "@/components/SubCalendarManageSheet";

// Mirror of PrimitiveTypeChips.TYPES — kept in sync there.
const PRIMITIVE_TYPES = [
  { id: "event",    label: "Events" },
  { id: "activity", label: "Activities" },
  { id: "study",    label: "Study" },
  { id: "chore",    label: "Chores" },
  { id: "todo",     label: "Todos" },
  { id: "poll",     label: "Polls" },
];
const ALL_TYPE_IDS = PRIMITIVE_TYPES.map((t) => t.id);

function typeStorageKey(familyId) {
  return familyId ? `kinly:cal-types:${familyId}` : null;
}

/**
 * The trigger button (data-testid="calendar-filters-button") that opens
 * the sheet. Shows a small "•" indicator when ANY non-default filter is
 * active so users notice they've narrowed the view.
 */
export function CalendarFiltersButton({
  onTypesChange,
  onCalendarsHiddenChange,
  refreshKey,
  hideTypes = false,
}) {
  const { family } = useAuth();
  const canCreateCal = usePermission("cal.create");
  const [open, setOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);

  // ── Primitive types ─────────────────────────────────────────────────
  const [enabledTypes, setEnabledTypes] = useState(() => new Set(ALL_TYPE_IDS));
  useEffect(() => {
    const k = typeStorageKey(family?.id);
    if (!k) return;
    try {
      const raw = localStorage.getItem(k);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) setEnabledTypes(new Set(arr));
      }
    } catch { /* keep defaults */ }
  }, [family?.id]);
  useEffect(() => {
    onTypesChange?.([...enabledTypes]);
  }, [enabledTypes, onTypesChange]);

  const toggleType = (id) => {
    setEnabledTypes((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      const k = typeStorageKey(family?.id);
      if (k) localStorage.setItem(k, JSON.stringify([...next]));
      return next;
    });
  };
  const resetTypes = () => {
    const next = new Set(ALL_TYPE_IDS);
    setEnabledTypes(next);
    const k = typeStorageKey(family?.id);
    if (k) localStorage.setItem(k, JSON.stringify([...next]));
  };

  // ── Sub-calendars ───────────────────────────────────────────────────
  const [calendars, setCalendars] = useState([]);
  const [hiddenCals, setHiddenCals] = useState(() => getHiddenCalendarIds(family?.id));
  useEffect(() => {
    if (!family?.id) return;
    listCalendars(family.id).then((data) => setCalendars(data || [])).catch(() => {});
  }, [family?.id, refreshKey]);
  useEffect(() => {
    onCalendarsHiddenChange?.([...hiddenCals]);
  }, [hiddenCals, onCalendarsHiddenChange]);

  const toggleCal = (id) => {
    const next = new Set(hiddenCals);
    if (next.has(id)) next.delete(id); else next.add(id);
    setHiddenCals(next);
    setHiddenCalendarIds(family?.id, next);
  };
  const showAllCals = () => {
    const empty = new Set();
    setHiddenCals(empty);
    setHiddenCalendarIds(family?.id, empty);
  };

  const typesNarrowed = enabledTypes.size < ALL_TYPE_IDS.length;
  const calsNarrowed = hiddenCals.size > 0;
  const anyActive = typesNarrowed || calsNarrowed;
  const activeCount = (typesNarrowed ? 1 : 0) + (calsNarrowed ? 1 : 0);

  return (
    <>
      <button
        type="button"
        data-testid="calendar-filters-button"
        onClick={() => setOpen(true)}
        className={[
          "shrink-0 relative w-9 h-9 rounded-full inline-flex items-center justify-center tap border transition-colors",
          anyActive
            ? "bg-primary/10 text-primary border-primary/30"
            : "bg-card text-foreground border-border hover:border-foreground/40",
        ].join(" ")}
        aria-label={`Filter${anyActive ? ` (${activeCount} active)` : ""}`}
      >
        <SlidersHorizontal className="w-4 h-4" />
        {anyActive && (
          <span
            data-testid="calendar-filters-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] px-1 rounded-full bg-primary text-white text-[9px] font-bold leading-[14px] text-center"
          >
            {activeCount}
          </span>
        )}
      </button>

      <BottomSheet open={open} onClose={() => setOpen(false)} title="Filter calendar">
        <div data-testid="calendar-filters-sheet" className="space-y-6 px-1 pb-4">

          {!hideTypes && (
            <section>
              <header className="flex items-center justify-between mb-2">
                <h3 className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-medium">
                  By type
                </h3>
                {typesNarrowed && (
                  <button
                    type="button"
                    data-testid="filter-types-reset"
                    onClick={resetTypes}
                    className="text-[11px] uppercase tracking-wider text-primary tap"
                  >
                    Show all
                  </button>
                )}
              </header>
              <ul className="grid grid-cols-2 gap-2">
                {PRIMITIVE_TYPES.map((t) => {
                  const on = enabledTypes.has(t.id);
                  return (
                    <li key={t.id}>
                      <button
                        type="button"
                        onClick={() => toggleType(t.id)}
                        data-testid={`filter-type-${t.id}`}
                        className={[
                          "w-full flex items-center justify-between gap-2 h-12 px-3 rounded-xl border text-sm tap transition-colors",
                          on
                            ? "bg-primary/10 border-primary/30 text-primary"
                            : "bg-card border-border text-muted-foreground",
                        ].join(" ")}
                        aria-pressed={on}
                      >
                        <span>{t.label}</span>
                        {on
                          ? <Check className="w-4 h-4 shrink-0" />
                          : <span className="w-4 h-4 rounded-full border border-current opacity-60 shrink-0" />
                        }
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          <section>
            <header className="flex items-center justify-between mb-2">
              <h3 className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-medium">
                By calendar
              </h3>
              <div className="flex items-center gap-3">
                {calsNarrowed && (
                  <button
                    type="button"
                    data-testid="filter-cals-reset"
                    onClick={showAllCals}
                    className="text-[11px] uppercase tracking-wider text-primary tap"
                  >
                    Show all
                  </button>
                )}
                {canCreateCal && (
                  <button
                    type="button"
                    onClick={() => setManageOpen(true)}
                    data-testid="filter-cals-manage"
                    className="text-[11px] uppercase tracking-wider text-muted-foreground tap inline-flex items-center gap-1"
                    aria-label="Manage sub-calendars"
                  >
                    <Settings2 className="w-3 h-3" /> Manage
                  </button>
                )}
              </div>
            </header>
            <ul className="space-y-1.5">
              {calendars.length === 0 && (
                <li className="text-xs text-muted-foreground py-2">
                  No sub-calendars yet — tap Manage to create one.
                </li>
              )}
              {calendars.map((c) => {
                const visible = !hiddenCals.has(c.id);
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => toggleCal(c.id)}
                      data-testid={`filter-cal-${c.id}`}
                      className={[
                        "w-full flex items-center gap-3 h-12 px-3 rounded-xl border text-sm tap transition-colors",
                        visible
                          ? "bg-primary/5 border-primary/20"
                          : "bg-card border-border opacity-60",
                      ].join(" ")}
                      aria-pressed={visible}
                    >
                      <span
                        className="w-3 h-3 rounded-full shrink-0"
                        style={{ background: c.color || "#6366f1" }}
                      />
                      <span className="flex-1 text-left truncate">{c.name}</span>
                      {visible
                        ? <Check className="w-4 h-4 text-primary shrink-0" />
                        : <X className="w-4 h-4 text-muted-foreground shrink-0" />
                      }
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>

        </div>
      </BottomSheet>

      <SubCalendarManageSheet
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        onChanged={() => {
          if (!family?.id) return;
          listCalendars(family.id).then((data) => setCalendars(data || [])).catch(() => {});
        }}
      />
    </>
  );
}
